import * as yup from '@if/core/yup'
import { ControlledCheckbox, Help } from '@if/mui'
import { Box, Stack, Typography } from '@mui/material'
import useFeatureFlags from '@src/data/api/featureSwitch-api/featureSwitch-api'
import { EBannerOwnedFieldKey, EMerchantProperties } from '@src/data/types/Constants'
import { FC, memo, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const propertiesConfig = [
  {
    key: EMerchantProperties.noPaymentPolicy,
    titleKey: 'merchant.noPaymentPolicy.title',
    descriptionKey: 'merchant.noPaymentPolicy.description',
  },
  {
    key: EMerchantProperties.hideAddress,
    titleKey: 'merchant.hideAddress.title',
    descriptionKey: 'merchant.hideAddress.description',
  },
  {
    key: EMerchantProperties.canSkipFlinks,
    titleKey: 'merchant.canSkipFlinks.title',
    descriptionKey: 'merchant.canSkipFlinks.description',
  },
  {
    key: EMerchantProperties.canPayBorrowers,
    titleKey: 'merchant.canPayBorrowers.title',
    descriptionKey: 'merchant.canPayBorrowers.description',
  },
  {
    key: EMerchantProperties.forcePaymentToBorrower,
    titleKey: 'merchant.forcePaymentToBorrower.title',
    descriptionKey: 'merchant.forcePaymentToBorrower.description',
  },
  {
    key: EMerchantProperties.automaticIncomeApproval,
    titleKey: 'merchant.automaticIncomeApproval.title',
    descriptionKey: 'merchant.automaticIncomeApproval.description',
  },
  {
    key: EMerchantProperties.canAcceptHighEstimate,
    titleKey: 'merchant.canAcceptHighEstimate.title',
    descriptionKey: 'merchant.canAcceptHighEstimate.description',
  },
  {
    key: EMerchantProperties.showInterestRateOnMerchantDashboard,
    titleKey: 'merchant.showInterestRate.title',
    descriptionKey: 'merchant.showInterestRate.description',
  },
  {
    key: EMerchantProperties.worksheetByMerchant,
    titleKey: 'merchant.worksheetByMerchant.title',
    descriptionKey: 'merchant.worksheetByMerchant.description',
  },
] as const

// Maps each editable merchant property to its corresponding banner-owned field key.
// Properties absent from this map (e.g. forcePaymentToBorrower) are never banner-owned.
const bannerFieldKeyMap: Partial<Record<EMerchantProperties, EBannerOwnedFieldKey>> = {
  [EMerchantProperties.noPaymentPolicy]: EBannerOwnedFieldKey.NoPmtPolicy,
  [EMerchantProperties.hideAddress]: EBannerOwnedFieldKey.HideAddress,
  [EMerchantProperties.canSkipFlinks]: EBannerOwnedFieldKey.SkipIncomeVerificationFlinks,
  [EMerchantProperties.canPayBorrowers]: EBannerOwnedFieldKey.CanPayBorrowers,
  [EMerchantProperties.automaticIncomeApproval]: EBannerOwnedFieldKey.AutoIncomeApproval,
  [EMerchantProperties.canAcceptHighEstimate]: EBannerOwnedFieldKey.CanUseHighEstimate,
  [EMerchantProperties.showInterestRateOnMerchantDashboard]: EBannerOwnedFieldKey.AlwaysShowInterestRateMerchDb,
  [EMerchantProperties.worksheetByMerchant]: EBannerOwnedFieldKey.WorksheetByMerchant,
}

const propertyConfigValidator = yup.boolean().required().default(false)

export const MerchantPropertiesSchema = yup.object().shape(
  propertiesConfig.reduce(
    (acc, curr) => {
      acc[curr.key] = propertyConfigValidator
      return acc
    },
    {} as Record<EMerchantProperties, typeof propertyConfigValidator>,
  ),
)

interface MerchantPropertiesPanelLabelProps {
  title: string
  description: string
  tooltip?: string
}

const MerchantPropertiesPanelLabel = memo(({ title, description, tooltip }: MerchantPropertiesPanelLabelProps) => {
  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={0.5}>
        <Typography>{title}</Typography>
        {tooltip && <Help title={tooltip} />}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  )
})
MerchantPropertiesPanelLabel.displayName = 'MerchantPropertiesPanelLabel'

interface Props {
  bannerOwnedFields: string[]
}

const MerchantPropertiesPanel: FC<Props> = ({ bannerOwnedFields }) => {
  const { t } = useTranslation()
  const [featureFlags] = useFeatureFlags()
  const { control, setValue, watch } = useFormContext<yup.InferSubmit<typeof MerchantPropertiesSchema>>()

  const canAcceptHighEstimate = watch(EMerchantProperties.canAcceptHighEstimate)
  const forcePaymentToBorrower = watch(EMerchantProperties.forcePaymentToBorrower)
  const worksheetByMerchant = watch(EMerchantProperties.worksheetByMerchant)
  const isReadOnlyCanPayBorrowers = forcePaymentToBorrower === true || canAcceptHighEstimate
  const isWorksheetByMerchantActive = featureFlags?.useHybridFlowWorksheetByMerchant === true && worksheetByMerchant

  const isBannerOwned = (key: EMerchantProperties): boolean => {
    const fieldKey = bannerFieldKeyMap[key]
    return fieldKey !== undefined && bannerOwnedFields.includes(fieldKey)
  }

  const getTooltip = (key: EMerchantProperties): string | undefined => {
    if (isBannerOwned(key)) return t('merchant.bannerOwnedTooltip')
    switch (key) {
      case EMerchantProperties.noPaymentPolicy:
        return isWorksheetByMerchantActive ? t('merchant.noPaymentPolicy.worksheetTooltip') : undefined
      case EMerchantProperties.canPayBorrowers:
        if (forcePaymentToBorrower) return t('merchant.canPayBorrowers.forcePaymentTooltip')
        if (canAcceptHighEstimate) return t('merchant.canPayBorrowers.highEstimateTooltip')
        return undefined
      default:
        return undefined
    }
  }

  useEffect(() => {
    if (canAcceptHighEstimate) {
      setValue(EMerchantProperties.noPaymentPolicy, false)
      setValue(EMerchantProperties.canPayBorrowers, true)
    }
  }, [canAcceptHighEstimate, setValue])

  useEffect(() => {
    if (isWorksheetByMerchantActive) {
      setValue(EMerchantProperties.noPaymentPolicy, false)
    }
  }, [isWorksheetByMerchantActive, setValue])

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('merchant.properties')}
      </Typography>

      <Stack gap={1}>
        <ControlledCheckbox
          name={EMerchantProperties.noPaymentPolicy}
          control={control}
          disabled={isWorksheetByMerchantActive || isBannerOwned(EMerchantProperties.noPaymentPolicy)}
          label={
            <MerchantPropertiesPanelLabel
              title={t(propertiesConfig[0].titleKey)}
              description={t(propertiesConfig[0].descriptionKey)}
              tooltip={getTooltip(EMerchantProperties.noPaymentPolicy)}
            />
          }
        />
        {featureFlags?.useHybridFlowWorksheetByMerchant && (
          <ControlledCheckbox
            name={EMerchantProperties.worksheetByMerchant}
            control={control}
            disabled
            label={
              <MerchantPropertiesPanelLabel
                title={t('merchant.worksheetByMerchant.title')}
                description={t('merchant.worksheetByMerchant.description')}
                tooltip={getTooltip(EMerchantProperties.worksheetByMerchant)}
              />
            }
          />
        )}
        <ControlledCheckbox
          name={EMerchantProperties.hideAddress}
          control={control}
          disabled={isBannerOwned(EMerchantProperties.hideAddress)}
          label={
            <MerchantPropertiesPanelLabel
              title={t(propertiesConfig[1].titleKey)}
              description={t(propertiesConfig[1].descriptionKey)}
              tooltip={getTooltip(EMerchantProperties.hideAddress)}
            />
          }
        />
        <ControlledCheckbox
          name={EMerchantProperties.canSkipFlinks}
          control={control}
          disabled={isBannerOwned(EMerchantProperties.canSkipFlinks)}
          label={
            <MerchantPropertiesPanelLabel
              title={t(propertiesConfig[2].titleKey)}
              description={t(propertiesConfig[2].descriptionKey)}
              tooltip={getTooltip(EMerchantProperties.canSkipFlinks)}
            />
          }
        />
        <ControlledCheckbox
          name={EMerchantProperties.canPayBorrowers}
          control={control}
          disabled={isReadOnlyCanPayBorrowers || isBannerOwned(EMerchantProperties.canPayBorrowers)}
          label={
            <MerchantPropertiesPanelLabel
              title={t(propertiesConfig[3].titleKey)}
              description={t(propertiesConfig[3].descriptionKey)}
              tooltip={getTooltip(EMerchantProperties.canPayBorrowers)}
            />
          }
        />
        <ControlledCheckbox
          name={EMerchantProperties.forcePaymentToBorrower}
          control={control}
          disabled
          label={
            <MerchantPropertiesPanelLabel
              title={t(propertiesConfig[4].titleKey)}
              description={t(propertiesConfig[4].descriptionKey)}
              tooltip={t('merchant.forcePaymentToBorrower.readOnlyTooltip')}
            />
          }
        />
        <ControlledCheckbox
          name={EMerchantProperties.automaticIncomeApproval}
          control={control}
          disabled={isBannerOwned(EMerchantProperties.automaticIncomeApproval)}
          label={
            <MerchantPropertiesPanelLabel
              title={t(propertiesConfig[5].titleKey)}
              description={t(propertiesConfig[5].descriptionKey)}
              tooltip={getTooltip(EMerchantProperties.automaticIncomeApproval)}
            />
          }
        />
        <ControlledCheckbox
          name={EMerchantProperties.canAcceptHighEstimate}
          control={control}
          disabled={isBannerOwned(EMerchantProperties.canAcceptHighEstimate)}
          label={
            <MerchantPropertiesPanelLabel
              title={t(propertiesConfig[6].titleKey)}
              description={t(propertiesConfig[6].descriptionKey)}
              tooltip={getTooltip(EMerchantProperties.canAcceptHighEstimate)}
            />
          }
        />
        {featureFlags?.useMerchDBshowRate && (
          <ControlledCheckbox
            name={EMerchantProperties.showInterestRateOnMerchantDashboard}
            control={control}
            disabled={isBannerOwned(EMerchantProperties.showInterestRateOnMerchantDashboard)}
            label={
              <MerchantPropertiesPanelLabel
                title={t(propertiesConfig[7].titleKey)}
                description={t(propertiesConfig[7].descriptionKey)}
                tooltip={getTooltip(EMerchantProperties.showInterestRateOnMerchantDashboard)}
              />
            }
          />
        )}
      </Stack>
    </Box>
  )
}

export default MerchantPropertiesPanel
